/**
 * 快照模块
 * 处理 API 变更检测和快照记录
 */
import * as fs from 'fs';
import * as prettier from 'prettier';
import Store from '@/store';
import {
  extractFunctionsFromCode,
  extractFunctionsFromFile,
  compareFunctions,
  generateMultiColumnMarkdownTable,
  FunctionInfo,
} from '@/helpers/countFunctions';
import {
  compareSwagger,
  generateComparisonMarkdown,
  generateDetailedDiffLog,
} from '@/core/swagger-comparator';

export interface AutoRequestOptions {
  filename: string;
  isTypeScript: boolean;
  
  // JsDoc 配置（仅 JavaScript 模式有效）
  jsdoc?: {
    enabled: boolean;  // 是否启用 JsDoc 类型注释
  };
  
  // 文件头尾代码（独立于 JsDoc）
  prefix?: string;     // 文件头部代码
  endfix?: string;     // 文件尾部代码
  
  // 自定义渲染函数
  renderMethodCall?: () => string;
  renderHeaderTemplate?: () => string;
  renderMethodJsDocCall?: () => string;
  renderEndTemplate?: () => string;
  
  // 日志和快照
  loggerPath?: string;           // 错误日志文件完整路径，如 './logs/errors.json'
  snapshotsPath?: string;        // 快照文件路径，如 './snapshots.md'
  swaggerBackupPath?: string;    // Swagger 备份路径，如 './swagger.json'
  
  // 其他配置
  skipPrompt?: boolean;          // 是否跳过确认提示
  ignoreUrls?: string[];         // URL 忽略列表（支持通配符）
  prettierFormat?: any;          // Prettier 格式化配置（不会覆盖 parser）
  total?: number;                // 内部计数器
  
  // ⚠️ 已废弃参数（向后兼容）
  /** @deprecated 请使用 loggerPath 直接指定完整路径 */
  loggerFileName?: string;
}

/**
 * 生成快照并检测 API 变更
 * @param snapFilePath - 快照文件路径（可选）
 * @param output - 输出目录
 * @param options - 配置选项
 * @param template - 模板代码
 * @param swaggerJson - 当前 swagger JSON 字符串（可选，用于 swagger 对比）
 * @returns 变更信息（新增和删除的函数）
 */
export const generatorSnapshots = async (
  snapFilePath: string | undefined,
  output: string,
  options: AutoRequestOptions,
  template: string,
  swaggerJson?: string
): Promise<{ removed: FunctionInfo[]; added: FunctionInfo[] }> => {
  // Swagger 对比结果
  let swaggerComparisonMarkdown = '';
  let useSwaggerComparison = false;
  
  // 如果提供了 swaggerJson 和 swaggerBackupPath，且备份文件存在，进行 swagger 对比
  if (swaggerJson && options.swaggerBackupPath && fs.existsSync(options.swaggerBackupPath)) {
    try {
      console.log(`📊 检测到历史 Swagger 文件，开始对比...`);
      
      // 读取旧的 swagger 文件
      const oldSwaggerJson = fs.readFileSync(options.swaggerBackupPath, 'utf-8');
      
      // 对比 swagger
      const comparison = compareSwagger(oldSwaggerJson, swaggerJson);
      
      // 生成对比结果
      const totalChanges = comparison.added.length + comparison.removed.length + comparison.modified.length;
      
      if (totalChanges > 0) {
        console.log(`\n📋 Swagger 对比结果:`);
        console.log(`   - 新增接口: ${comparison.added.length} 个`);
        console.log(`   - 删除接口: ${comparison.removed.length} 个`);
        console.log(`   - 修改接口: ${comparison.modified.length} 个（数据结构发生变化）`);
        console.log('');
        
        // 生成详细的 diff 日志
        const diffLog = generateDetailedDiffLog(comparison);
        
        // 如果有 loggerPath，保存详细的 diff 日志
        if (options.loggerPath && comparison.modified.length > 0) {
          try {
            // 确保日志目录存在
            const logDir = options.loggerPath.substring(0, options.loggerPath.lastIndexOf('/'));
            if (logDir && !fs.existsSync(logDir)) {
              fs.mkdirSync(logDir, { recursive: true });
            }
            
            // 读取现有日志
            let existingLog: any = {};
            if (fs.existsSync(options.loggerPath)) {
              try {
                existingLog = JSON.parse(fs.readFileSync(options.loggerPath, 'utf-8'));
              } catch {
                existingLog = {};
              }
            }
            
            // 添加时间戳
            const timestamp = new Date().toISOString();
            existingLog[`swagger_diff_${timestamp}`] = diffLog;
            
            // 写入日志文件
            fs.writeFileSync(options.loggerPath, JSON.stringify(existingLog, null, 2), 'utf-8');
            console.log(`📝 详细的 diff 日志已保存到: ${options.loggerPath}`);
            console.log(`   提示: 修改的接口包含完整的新旧数据结构对比，可使用 diff 工具查看具体变化\n`);
          } catch (error) {
            console.warn(`⚠️  保存 diff 日志失败: ${(error as Error).message}`);
          }
        }
        
        // 生成包含详细 diff 的 markdown（用于 snapshots 文件）
        swaggerComparisonMarkdown = '## Swagger 文档变更\n\n' + generateComparisonMarkdown(comparison, true);
        useSwaggerComparison = true;
      } else {
        console.log(`✅ Swagger 对比完成: 无变化\n`);
        useSwaggerComparison = true;
      }
    } catch (error) {
      console.warn(`⚠️  Swagger 对比失败: ${(error as Error).message}，将使用代码对比`);
    }
  } else if (swaggerJson && options.swaggerBackupPath) {
    console.log(`💡 首次生成，将保存当前 Swagger 作为基准\n`);
  }

  // 如果没有使用 swagger 对比，则使用代码对比
  let info = { removed: [] as FunctionInfo[], added: [] as FunctionInfo[] };
  if (!useSwaggerComparison) {
    const outputFilePath = `${output}${options.filename}${Store.getFileType()}`;
    const oldFileInfo = extractFunctionsFromFile(outputFilePath);
    const newFileInfo = extractFunctionsFromCode(template);
    info = compareFunctions(oldFileInfo, newFileInfo);
  }

  // 生成新增和删除的 API 信息
  const removeApis = info.removed.map((item) => [item.name, item.description, item.summary, '删除']);
  const addApis = info.added.map((item) => [item.name, item.description, item.summary, '新增']);

  // 生成 Markdown 表格
  const multiRemoveColumnTable = generateMultiColumnMarkdownTable(
    removeApis,
    ['函数名', '描述', '备注', '状态'],
    '删除函数'
  );
  const multiAddColumnTable = generateMultiColumnMarkdownTable(addApis, ['函数名', '描述', '备注', '状态'], '新增函数');

  // 合并表格内容（包括 swagger 对比结果）
  const contentParts = [];
  
  // 添加 swagger 对比结果
  if (swaggerComparisonMarkdown) {
    contentParts.push(swaggerComparisonMarkdown);
  }
  
  // 添加代码函数对比结果
  if (removeApis.length > 0 || addApis.length > 0) {
    contentParts.push('## 生成代码变更\n');
    if (removeApis.length > 0) {
      contentParts.push(multiRemoveColumnTable);
    }
    if (addApis.length > 0) {
      contentParts.push(multiAddColumnTable);
    }
  }
  
  const newContent = contentParts.join('\n\n');

  // 提示用户新增和删除的函数信息
  console.log(`【tips】删除函数：${removeApis.length}个函数，新增了函数：${addApis.length}个函数`);

  if (removeApis.length > 0) {
    console.log('删除的函数：');
    console.table(removeApis);
  }

  if (addApis.length > 0) {
    console.log('新增的函数：');
    console.table(addApis);
  }

  // 如果传入了 snapFilePath，则将内容写入文件
  if (snapFilePath) {
    // 添加时间戳
    const timestamp = new Date().toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const timestampHeader = `## ${timestamp}\n\n`;
    const finalContent = timestampHeader + newContent + '\n\n';

    // 读取旧内容（如果文件存在）
    let oldContent = '';
    if (fs.existsSync(snapFilePath)) {
      oldContent = fs.readFileSync(snapFilePath, 'utf-8');
    }

    // 将新内容添加到旧内容顶部
    const fullContent = finalContent + oldContent;

    // 使用 Prettier 格式化并写入文件
    // 合并用户配置，但确保 parser 不被覆盖
    const prettierOptions = { ...options.prettierFormat, parser: 'markdown' };
    fs.writeFileSync(snapFilePath, await prettier.format(fullContent, prettierOptions));

    console.log(`【tips】查看具体信息请查看文件：${snapFilePath}`);
  } else {
    console.log('【tips】如需保存详细信息，请添加 snapFilePath 参数，如 ./snapshots.md');
  }
  return { removed: info.removed, added: info.added };
};

